/**
 * @voltxampere/engine — MOSFET AC small-signal stamp (Sprint 22 D4)
 *
 * Linearised around the DC operating point using the standard
 * Shichman-Hodges saturation model plus Meyer-saturation gate
 * capacitances:
 *
 *     gm  = √(2 · K_p · (W/L) · |I_D|)   (saturation)
 *     gds = λ · |I_D|                     (channel-length modulation)
 *     Cgs = Cgs0·W + (2/3)·Cox·W·L        (gate-source, Meyer)
 *     Cgd = Cgd0·W                         (gate-drain overlap)
 *
 *  Cap defaults are zero — Sprint 22 D-22-6 zero-diff guarantee.
 *  When set, Cgs/Cgd add the standard hybrid-π high-frequency
 *  rolloff (gate input no longer presents an open circuit at AC).
 *
 *  KCL contributions for terminals G (gate), D (drain), S (source):
 *
 *    Gate: zero current (isolated at AC without Cgs/Cgd).
 *    Y[D,G] += gm
 *    Y[D,D] += gds
 *    Y[D,S] -= (gm + gds)
 *    Y[S,G] -= gm
 *    Y[S,D] -= gds
 *    Y[S,S] += (gm + gds)
 *
 *  Identical for NMOS and PMOS — polarity flip is absorbed by
 *  computing `|I_D|`.
 *
 *  Edge cases:
 *  - `|I_D|` below 1 fA → device effectively off, use floor to keep
 *    AC matrix non-singular.
 *  - `λ == 0` → ro infinite, gds suppressed.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
const I_FLOOR = 1e-15;
export const mosfetACStamp = (ctx, component) => {
    const Kp = getNumericParam(component, "Kp", 2e-5);
    const W = getNumericParam(component, "W", 10e-6);
    const L = getNumericParam(component, "L", 1e-6);
    const Lambda = getNumericParam(component, "Lambda", 0.01);
    const Vt0 = getNumericParam(component, "Vt0", 0.7);
    if (!(Kp > 0) || !(W > 0) || !(L > 0)) {
        throw new StampParameterError(`mosfetACStamp: component "${component.id}" has non-positive Kp/W/L (got Kp=${String(Kp)}, W=${String(W)}, L=${String(L)})`, {
            meta: {
                stampType: "mosfet",
                parameter: "Kp/W/L",
                received: { Kp, W, L },
                expected: "all > 0",
                componentId: component.id,
            },
        });
    }
    const gateNet = getTerminalNetId(component, "gate");
    const drainNet = getTerminalNetId(component, "drain");
    const sourceNet = getTerminalNetId(component, "source");
    const G = ctx.nodeIndex(gateNet);
    const D = ctx.nodeIndex(drainNet);
    const S = ctx.nodeIndex(sourceNet);
    const Vgs = (ctx.dcNodeVoltages.get(gateNet) ?? 0) - (ctx.dcNodeVoltages.get(sourceNet) ?? 0);
    const Vov = Math.abs(Vgs) - Math.abs(Vt0);
    // Saturation drain current (cut-off if Vov ≤ 0).
    const Id = Vov > 0 ? Math.max(0.5 * Kp * (W / L) * Vov * Vov, I_FLOOR) : I_FLOOR;
    const gm = Math.sqrt(2 * Kp * (W / L) * Id);
    const gds = Lambda > 0 ? Lambda * Id : 0;
    ctx.addY(D, G, gm, 0);
    ctx.addY(D, D, gds, 0);
    ctx.addY(D, S, -(gm + gds), 0);
    ctx.addY(S, G, -gm, 0);
    ctx.addY(S, D, -gds, 0);
    ctx.addY(S, S, gm + gds, 0);
    // Sprint 22 D4 — Meyer-saturation gate caps. Zero-default keeps
    // Sprint 5/6/7/20 fixtures bit-identical (no imaginary
    // admittance contribution unless the user opts in).
    const caps = readMosfetCapParams(component.params);
    const Cgs = caps.Cgs0 * W + (2 / 3) * caps.Cox * W * L;
    const Cgd = caps.Cgd0 * W;
    const bGs = ctx.omega * Cgs;
    const bGd = ctx.omega * Cgd;
    if (bGs !== 0) {
        ctx.addY(G, G, 0, bGs);
        ctx.addY(S, S, 0, bGs);
        ctx.addY(G, S, 0, -bGs);
        ctx.addY(S, G, 0, -bGs);
    }
    if (bGd !== 0) {
        ctx.addY(G, G, 0, bGd);
        ctx.addY(D, D, 0, bGd);
        ctx.addY(G, D, 0, -bGd);
        ctx.addY(D, G, 0, -bGd);
    }
};
function readOptionalNumber(params, key, fallback, predicate) {
    const raw = params[key];
    if (typeof raw !== "number" || !Number.isFinite(raw))
        return fallback;
    return predicate(raw) ? raw : fallback;
}
function readMosfetCapParams(params) {
    return {
        Cgs0: readOptionalNumber(params, "Cgs0", 0, (v) => v >= 0),
        Cgd0: readOptionalNumber(params, "Cgd0", 0, (v) => v >= 0),
        Cox: readOptionalNumber(params, "Cox", 0, (v) => v >= 0),
    };
}
//# sourceMappingURL=mosfet.js.map